import { useContext, useEffect, useRef, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { useSearchParams } from 'react-router-dom';

import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { PrimaryContext } from '@/services';
import { AspirationsToday } from '@/types.gql';
import { useContextMenu } from '@/utils/context-menu';

import { useAspirationContext } from '../hooks/useAspirationContext';
import { updateAspirationTimeZones } from '../utils';
import { useColumnDefs } from '../utils/columnDefs';

export type AspirationGridProps = {
  className?: string;
  setShowForm: (value: boolean) => void;
};

export const AspirationGrid = ({
  className,
  setShowForm,
}: AspirationGridProps) => {
  const columnDefs = useColumnDefs();
  const defaultGridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { quickSearchValue, setSelectedAspiration, aspirations } =
    useAspirationContext();
  const gridRef = useRef<AgGridReact>(null);

  const [params] = useSearchParams();
  const timeZone = params.get('tz') || '';
  const aspirationId = params.get('aId');

  const [gridAspirations, setGridAspirations] = useState<AspirationsToday[]>(
    [],
  );
  const [originalTimeZone, setOriginalTimeZone] = useState<string | null>(null);
  const [isFirstTimeZoneSelection, setIsFirstTimeZoneSelection] =
    useState(true);

  useEffect(() => {
    if (aspirations) {
      if (timeZone === '') {
        setGridAspirations(aspirations);
        return;
      }

      if (isFirstTimeZoneSelection) {
        setOriginalTimeZone(timeZone);
        setGridAspirations(aspirations);
        setIsFirstTimeZoneSelection(false);
      } else {
        if (originalTimeZone !== timeZone) {
          const updatedAspirations = updateAspirationTimeZones(
            aspirations,
            timeZone,
            originalTimeZone as string,
          );

          setGridAspirations(updatedAspirations);
        } else {
          setGridAspirations(aspirations);
        }
      }
    }
  }, [timeZone, aspirations, isFirstTimeZoneSelection, originalTimeZone]);

  useEffect(() => {
    if (aspirationId && aspirations) {
      const matchingAspiration = aspirations.find(
        (asp) => asp.aspirationId === aspirationId,
      );
      if (matchingAspiration) {
        setSelectedAspiration(matchingAspiration);
        setShowForm(true);
      }
    }
  }, [aspirationId, aspirations, setSelectedAspiration, setShowForm]);

  useEffect(() => {
    if (!aspirationId) {
      setShowForm(false);
    }
  }, [aspirationId, params, setShowForm]);

  useEffect(() => {
    gridRef.current?.api?.paginationGoToFirstPage();
  }, [gridAspirations, quickSearchValue]);

  const { getContextMenuItems } = useContextMenu({ idKey: 'aspirationId' });

  return (
    <div className={`h-[590px] w-full pb-6`}>
      <WithLocalStorage storageKey="aspiration-grid">
        <AgGridReact
          ref={gridRef}
          className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} ${className}`.trim()}
          defaultColDef={defaultColDef}
          rowData={gridAspirations}
          overlayLoadingTemplate="Please include date, site, and technician"
          columnDefs={columnDefs}
          quickFilterText={quickSearchValue}
          gridOptions={defaultGridOptions}
          context={{ setShowForm, timeZone }}
          getRowId={(params) => params.data.aspirationId}
          pagination={true}
          paginationPageSize={100}
          paginationPageSizeSelector={[20, 50, 100, 400]}
          onFirstDataRendered={() =>
            gridRef.current?.api?.paginationGoToFirstPage()
          }
          getContextMenuItems={getContextMenuItems}
        />
      </WithLocalStorage>
    </div>
  );
};
